import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Card,
  CardHeader,
  Typography,
  Button,
  IconButton,
  Divider,
  CircularProgress,
} from "@mui/material";
import { DoneAll, OpenInNew } from "@mui/icons-material";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

import {
  fetchNotifications,
  markAsRead,
  markAllRead,
  selectNotifications,
  selectUnreadCount,
} from "../../redux/slices/notificationSlice";
import PageHeader from "../../components/common/PageHeader";
import EmptyState from "../../components/common/EmptyState";
import SkeletonLoader from "../../components/common/SkeletonLoader";

dayjs.extend(relativeTime);

// `bg` values are MUI theme token strings (resolved wherever they're used
// as an sx `bgcolor`), not literal hex — so each adapts to light/dark mode
// automatically instead of staying a fixed pastel.
const NOTIF_ICONS = {
  shipment_update: { icon: "📦", bg: "info.light" },
  sailing_update: { icon: "⛵", bg: "rgba(147,51,234,0.14)" },
  payment_reminder: { icon: "💳", bg: "error.light" },
  customs_alert: { icon: "📋", bg: "warning.light" },
  document_required: { icon: "⚠️", bg: "warning.light" },
  pickup_ready: { icon: "🎉", bg: "success.light" },
  general: { icon: "🔔", bg: "action.hover" },
};

const CustomerNotifications = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const notifications = useSelector(selectNotifications);
  const unreadCount = useSelector(selectUnreadCount);
  const [loading, setLoading] = React.useState(true);

  useEffect(() => {
    setLoading(true);
    dispatch(fetchNotifications({ limit: 50 })).finally(() => setLoading(false));
  }, [dispatch]);

  const handleMarkRead = (id) => dispatch(markAsRead(id));
  const handleMarkAll = () => dispatch(markAllRead());

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <PageHeader
        title="Notifications"
        subtitle={unreadCount > 0 ? `${unreadCount} unread` : "All caught up"}
        action={
          unreadCount > 0 && (
            <Button
              size="small"
              startIcon={<DoneAll />}
              variant="outlined"
              onClick={handleMarkAll}
            >
              Mark All Read
            </Button>
          )
        }
      />

      <Card>
        {loading ? (
          <SkeletonLoader rows={4} />
        ) : notifications.length === 0 ? (
          <EmptyState
            icon="🔔"
            title="No notifications"
            description="You're all caught up! We'll notify you when there are updates on your shipments."
          />
        ) : (
          notifications.map((n, idx) => {
            const config = NOTIF_ICONS[n.type] || NOTIF_ICONS.general;
            return (
              <Box key={n._id}>
                <Box
                  onClick={() => {
                    if (!n.isRead) handleMarkRead(n._id);
                    if (n.actionUrl) navigate(n.actionUrl);
                  }}
                  sx={{
                    display: "flex",
                    gap: 1.5,
                    px: 2.5,
                    py: 2,
                    bgcolor: n.isRead ? "transparent" : "rgba(13,98,155,0.03)",
                    cursor: "pointer",
                    transition: "background 0.1s",
                    "&:hover": { bgcolor: "action.hover" },
                  }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "50%",
                      bgcolor: config.bg,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 18,
                      flexShrink: 0,
                    }}
                  >
                    {config.icon}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: 13.5,
                        fontWeight: n.isRead ? 500 : 700,
                        color: n.isRead ? "text.secondary" : "text.primary",
                      }}
                    >
                      {n.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 0.25 }}
                    >
                      {n.message}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.disabled"
                      sx={{ mt: 0.5, display: "block" }}
                    >
                      {dayjs(n.createdAt).fromNow()}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-end",
                      gap: 1,
                      flexShrink: 0,
                    }}
                  >
                    {!n.isRead && (
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          bgcolor: "primary.main",
                        }}
                      />
                    )}
                    {n.shipment && (
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(
                            `/dashboard/shipments/${n.shipment.trackingNumber || n.shipment}`,
                          );
                        }}
                      >
                        <OpenInNew sx={{ fontSize: 14 }} />
                      </IconButton>
                    )}
                  </Box>
                </Box>
                {idx < notifications.length - 1 && <Divider />}
              </Box>
            );
          })
        )}
      </Card>
    </Box>
  );
};

export default CustomerNotifications;
